import React, { useEffect, useState } from "react";
import { FaMinus, FaPlus, FaFolderOpen } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useCart } from "./context/CartContext";
import { apiUrl } from "../config/config";
import ProductDescriptionTabs from "./ProductDescriptionTabs";
import SEO from "./SEO";
import RelatedProduct from "./home/RelatedProduct";
import ShopDetailSkeleton from "./ShopDetailSkeleton";

const ShopDetail = () => {
  const ids = window.location.pathname.split("/").pop(); // last param
  const navigate = useNavigate();
  const pathParts = window.location.pathname.split("/");
  const { refreshCart } = useCart();
  const id = pathParts.pop(); // last param (16)
  const categoryId = pathParts[3]; // Get category ID from URL (2)

  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [width, setWidth] = useState(1);
  const [height, setHeight] = useState(1);
  const [qty, setQty] = useState(25);

  const [previewImage, setPreviewImage] = useState(null);
  const [fileObject, setFileObject] = useState(null);

  // store selected filter values (color, shape, etc.)
  const [selectedFilters, setSelectedFilters] = useState({});

  // optional inputs
  const [inputs, setInputs] = useState({
    border_thread_color: "",
    special_notes: "",
  });

  const getSelectedFiltersPrice = (filterbars, selectedFilters) => {
    let total = 0;

    (filterbars || []).forEach((bar) => {
      const selectedValue = selectedFilters[bar.label];
      if (!selectedValue) return;

      const option = bar.options?.find((opt) => opt.name === selectedValue);

      if (option?.price) {
        total += Number(option.price);
      }
    });

    return total;
  };

  // --------------------------
  // FETCH PRODUCT BY ID
  // --------------------------
  useEffect(() => {
    fetch(`${apiUrl}/api/products/${id}`)
      .then((res) => res.json())
      .then((res) => {
        const p = res.data;
        setProduct(p);

        if (p?.image) {
          setActiveImage(`${apiUrl}/storage/${p.image}`);
        }

        setWidth(Number(p?.filter?.width_min || 1));
        setHeight(Number(p?.filter?.height_min || 1));

        // 🔥 AUTO SELECT FIRST OPTION OF EACH FILTER BAR
        if (p?.filter?.filterbars) {
          const defaults = {};

          p.filter.filterbars.forEach((bar) => {
            if (bar.options && bar.options.length > 0) {
              defaults[bar.label] = bar.options[0].name;
            }
          });

          setSelectedFilters(defaults);
        }
      })
      .catch((err) => console.log(err));
  }, [id]);

  if (!product) return <ShopDetailSkeleton />;

  const p = product;
  const filter = p?.filter || {};

  // --------------------------
  // PRICE CALCULATION
  // --------------------------

  const getSizeAvg = (w, h) => (w + h) / 2;

  const getBasePrice = (rules, w, h) => {
    const size = getSizeAvg(w, h);
    for (let r of rules || []) {
      if (size >= parseFloat(r.min) && size <= parseFloat(r.max)) {
        return parseFloat(r.price);
      }
    }
    return 0;
  };

  const getDiscountPercent = (discounts, qty) => {
    let discount = 0;
    (discounts || []).forEach((d) => {
      if (qty >= Number(d.qty)) discount = Number(d.discount);
    });
    return discount;
  };

  const basePrice = getBasePrice(filter.rules, width, height);

  // 🔥 ADD FILTER PRICE
  const selectedFiltersPrice = getSelectedFiltersPrice(
    filter.filterbars,
    selectedFilters
  );

  const priceBeforeDiscount = basePrice + selectedFiltersPrice;

  const discountPercent = getDiscountPercent(filter.discounts, qty);

  const finalUnitPrice =
    priceBeforeDiscount - (priceBeforeDiscount * discountPercent) / 100;

  const totalPrice = finalUnitPrice * qty;

  // ---------------------------------------
  // USER ID / GUEST ID LOGIC
  // ---------------------------------------
  const getUserOrGuestId = () => {
    // 1. CHECK IF USER IS LOGGED IN
    const userData = localStorage.getItem("user");
    console.log(userData, "userData");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user?.id) return { user_id: user.id, guest_id: null };
      } catch (err) {
        console.error("Invalid user format in localStorage");
      }
    }

    // 2. IF NO USER → CHECK FOR SAVED GUEST ID
    let guestId = localStorage.getItem("guest_id");
    if (!guestId) {
      // create random guest id
      guestId = "guest_" + Math.random().toString(36).substring(2, 12);
      localStorage.setItem("guest_id", guestId);
    }

    return { user_id: null, guest_id: guestId };
  };

  // --------------------------
  // ADD TO CART API
  // --------------------------
  const addToCart = async () => {
    const formData = new FormData();

    formData.append("product_id", p.id);
    formData.append("product_name", p.name);
    formData.append("qty", qty);
    formData.append("price", totalPrice.toFixed(2));

    // ONLY send width & height if price rules are enabled
    if (filter?.price_rules_enabled) {
      formData.append("width", width);
      formData.append("height", height);
    }

    formData.append("selected_filters", JSON.stringify(selectedFilters));
    formData.append("inputs", JSON.stringify(inputs));

    if (fileObject) {
      formData.append("upload_image", fileObject);
    }

    // Auto add user/guest ids
    const { user_id, guest_id } = getUserOrGuestId();
    if (user_id) formData.append("user_id", user_id);
    if (guest_id) formData.append("guest_id", guest_id);

    try {
      const res = await fetch(`${apiUrl}/api/cart/add`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.success) {
        refreshCart();
        navigate("/cart"); // 🔥 Smooth redirect
      } else {
        alert("Something went wrong.");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
    }
  };

  const handleQtyChange = (value) => {
    const val = Number(value);
    setQty(val < 25 ? 25 : val);
  };

  // BUY NOW → Same data, redirect checkout
  const buyNow = async () => {
    const success = await addToCart();
    if (success) navigate("/checkout");
  };

  // --------------------------
  // UI
  // --------------------------

  return (
    <>
      <SEO
        title={product.meta_title || product.name}
        description={
          product.meta_description ||
          product.otherDescription?.replace(/<[^>]+>/g, "").slice(0, 160)
        }
        keywords={product.meta_keywords || product.name}
      />

      <div className="bg-[#f5f6f5]">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-10 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8">
          {/* LEFT IMAGE SECTION */}
          <div className="self-start lg:sticky lg:top-10">
            <div className="overflow-hidden rounded-[28px] border border-[#1e242a]/10 bg-white p-4 shadow-[0_20px_55px_rgba(30,36,42,0.10)] sm:p-5">
              <div className="flex min-h-[360px] items-center justify-center rounded-2xl bg-[#e5e8e6] p-6 sm:min-h-[460px]">
                <img
                  src={activeImage || ""}
                  alt={p?.name || ""}
                  className="max-h-[420px] w-full object-contain"
                />
              </div>

              {/* GALLERY */}
              <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7">
                {[
                  ...(p?.image ? [`${apiUrl}/storage/${p.image}`] : []),
                  ...(p?.galleryImages?.map((img) => `${apiUrl}/storage/${img}`) ||
                    []),
                ]
                  .slice(0, 7)
                  .map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveImage(img)}
                      className={`aspect-square overflow-hidden rounded-xl border bg-white p-1 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${
                        activeImage === img
                          ? "border-[#1e242a] ring-2 ring-[#1e242a]/15"
                          : "border-[#1e242a]/10"
                      }`}
                    >
                      <img
                        src={img}
                        className="h-full w-full rounded-lg object-cover"
                        alt=""
                      />
                    </button>
                  ))}
              </div>
            </div>
          </div>

          {/* RIGHT SECTION */}
          <div className="self-start lg:sticky lg:top-10">
            <div className="rounded-[28px] border border-[#1e242a]/10 bg-white p-5 shadow-[0_20px_55px_rgba(30,36,42,0.10)] sm:p-7">
              <span className="inline-flex rounded-full border border-[#1e242a]/10 bg-[#e5e8e6] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#37404a]">
                Product Details
              </span>

              <h1 className="mt-4 text-3xl font-bold leading-tight text-[#1e242a]">
                {p?.name}
              </h1>

              <p className="mt-4 leading-relaxed text-[#5f6870]">
                {p?.description_paragraph}
              </p>

              {/* FILTER BARS */}
              {filter?.filterbars?.map((bar) => (
                <div className="mt-6" key={bar.label}>
                  <h2 className="mb-4 font-semibold capitalize text-[#1e242a]">
                    {bar.label}
                  </h2>

                  <div className="flex flex-wrap gap-3">
                    {bar.options?.map((opt, i) => (
                      <button
                        key={i}
                        onClick={() =>
                          setSelectedFilters({
                            ...selectedFilters,
                            [bar.label]: opt.name,
                          })
                        }
                        className={`flex w-20 flex-col items-center justify-center rounded-xl border px-1 py-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-md focus:outline-none ${
                          selectedFilters[bar.label] === opt.name
                            ? "border-[#1e242a] bg-[#e5e8e6] shadow-inner"
                            : "border-[#1e242a]/10 bg-white hover:border-[#1e242a]"
                        }`}
                      >
                        <img
                          src={`${apiUrl}/storage/${opt.image}`}
                          className="mb-2 h-12 w-12 rounded-full object-cover"
                          alt=""
                        />
                        <p className="text-center text-[0.6rem] font-medium text-[#1e242a]">
                          {opt.name}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* BORDER THREAD COLOR */}
              {filter?.border_thread_color && (
                <div className="mt-6">
                  <label className="font-semibold text-[#1e242a]">
                    Border Thread Color
                  </label>
                  <input
                    type="text"
                    className="mt-2 w-full rounded-xl border border-[#1e242a]/15 bg-[#f5f6f5] p-3 text-[#1e242a] shadow-sm outline-none transition duration-300 placeholder:text-[#5f6870] focus:border-[#37404a] focus:bg-white focus:ring-2 focus:ring-[#37404a]/25"
                    placeholder="Enter border thread color"
                    onChange={(e) =>
                      setInputs({ ...inputs, border_thread_color: e.target.value })
                    }
                  />
                </div>
              )}

              {/* SPECIAL NOTES */}
              {filter?.special_notes && (
                <div className="mt-6">
                  <label className="font-semibold text-[#1e242a]">
                    Special Notes
                  </label>
                  <textarea
                    className="mt-2 w-full resize-none rounded-xl border border-[#1e242a]/15 bg-[#f5f6f5] p-3 text-[#1e242a] shadow-sm outline-none transition duration-300 placeholder:text-[#5f6870] focus:border-[#37404a] focus:bg-white focus:ring-2 focus:ring-[#37404a]/25"
                    rows="4"
                    placeholder="Add your special notes..."
                    onChange={(e) =>
                      setInputs({ ...inputs, special_notes: e.target.value })
                    }
                  />
                </div>
              )}

              {/* UPLOAD IMAGE */}
              {filter?.upload_image && (
                <div className="mt-8 rounded-2xl border border-[#1e242a]/10 bg-[#f5f6f5] p-6 text-center shadow-sm">
                  <FaFolderOpen className="mx-auto mb-4 text-6xl text-[#37404a]" />

                  <label
                    htmlFor="uploadImage"
                    className="mt-4 inline-block cursor-pointer rounded-xl bg-[#1e242a] px-8 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#37404a]"
                  >
                    Upload Image
                  </label>

                  <input
                    id="uploadImage"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFileObject(file);
                      if (file) setPreviewImage(URL.createObjectURL(file));
                    }}
                  />

                  {previewImage && (
                    <div className="mt-6">
                      <img
                        src={previewImage}
                        className="mx-auto h-44 w-44 rounded-xl border border-[#1e242a]/10 object-cover shadow-lg"
                        alt=""
                      />
                    </div>
                  )}
                </div>
              )}

              {/* WIDTH & HEIGHT */}
              {filter?.price_rules_enabled && (
                <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="rounded-2xl border border-[#1e242a]/10 bg-[#f5f6f5] p-4">
                    <label className="mb-2 block font-semibold text-[#1e242a]">
                      Width (Inches):
                    </label>

                    <div className="flex w-full max-w-[180px] items-center justify-between rounded-xl border border-[#1e242a]/10 bg-white px-4 py-2">
                      <button
                        className="text-xl text-[#1e242a]"
                        onClick={() =>
                          setWidth(Math.max(filter.width_min || 1, width - 0.5))
                        }
                      >
                        –
                      </button>

                      <span className="text-lg font-semibold text-[#1e242a]">
                        {width}
                      </span>

                      <button
                        className="text-xl text-[#1e242a]"
                        onClick={() =>
                          setWidth(Math.min(filter.width_max || 100, width + 0.5))
                        }
                      >
                        +
                      </button>
                    </div>

                    <span className="center mt-2 block text-sm text-[#5f6870]">
                      Min - {filter.width_min}, Max {filter.width_max}
                    </span>
                  </div>

                  {/* HEIGHT */}
                  <div className="rounded-2xl border border-[#1e242a]/10 bg-[#f5f6f5] p-4">
                    <label className="mb-2 block font-semibold text-[#1e242a]">
                      Height (Inches):
                    </label>

                    <div className="flex w-full max-w-[180px] items-center justify-between rounded-xl border border-[#1e242a]/10 bg-white px-4 py-2">
                      <button
                        className="text-xl text-[#1e242a]"
                        onClick={() =>
                          setHeight(
                            Math.max(filter.height_min || 1, height - 0.5)
                          )
                        }
                      >
                        –
                      </button>

                      <span className="text-lg font-semibold text-[#1e242a]">
                        {height}
                      </span>

                      <button
                        className="text-xl text-[#1e242a]"
                        onClick={() =>
                          setHeight(
                            Math.min(filter.height_max || 100, height + 0.5)
                          )
                        }
                      >
                        +
                      </button>
                    </div>

                    <span className="center mt-2 block text-sm text-[#5f6870]">
                      Min - {filter.height_min}, Max - {filter.height_max}
                    </span>
                  </div>
                </div>
              )}

              {filter?.discounts && (
                <div className="mt-6 overflow-hidden rounded-2xl border border-[#1e242a]/10">
                  <table className="w-full">
                    <thead className="bg-[#1e242a] text-white">
                      <tr>
                        <th className="p-3 text-sm font-semibold">Qty</th>
                        <th className="p-3 text-sm font-semibold">Discount (%)</th>
                        <th className="p-3 text-sm font-semibold">
                          Price After Discount
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {filter.discounts.map((d, i) => {
                        const priceBeforeDiscount =
                          basePrice + selectedFiltersPrice;

                        const discountedPrice =
                          priceBeforeDiscount -
                          (priceBeforeDiscount * Number(d.discount)) / 100;

                        return (
                          <tr
                            key={i}
                            className={`text-center ${
                              i % 2 === 0 ? "bg-[#f5f6f5]" : "bg-white"
                            }`}
                          >
                            <td className="border-t border-[#1e242a]/10 p-3 text-[#5f6870]">
                              {d.qty}
                            </td>
                            <td className="border-t border-[#1e242a]/10 p-3 text-[#5f6870]">
                              {d.discount}%
                            </td>
                            <td className="border-t border-[#1e242a]/10 p-3 font-semibold text-[#1e242a]">
                              ${discountedPrice.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* QUANTITY + PRICE */}
              <div className="mt-10 flex flex-col gap-5 rounded-2xl border border-[#1e242a]/10 bg-[#f5f6f5] p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <label className="mb-1 block font-semibold text-[#1e242a]">
                    Quantity
                  </label>

                  <div className="flex w-44 items-center justify-between rounded-xl border border-[#1e242a]/10 bg-white px-3 py-2">
                    <button
                      className="px-2 text-xl text-[#1e242a]"
                      onClick={() => setQty(Math.max(25, qty - 1))}
                    >
                      –
                    </button>

                    <input
                      type="number"
                      min="25"
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val < 25 || isNaN(val)) {
                          setQty(25);
                        } else {
                          setQty(val);
                        }
                      }}
                      className="w-16 bg-transparent text-center text-lg text-[#1e242a] outline-none"
                    />

                    <button
                      className="px-2 text-xl text-[#1e242a]"
                      onClick={() => setQty(qty + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="text-left sm:text-right">
                  <p className="text-md text-[#5f6870]">
                    Base Price:
                    <span className="font-semibold text-[#1e242a]">
                      ${(basePrice + selectedFiltersPrice).toFixed(2)}
                    </span>
                  </p>

                  <p className="text-md mt-1 text-[#5f6870]">
                    {`$${finalUnitPrice.toFixed(2)} × ${qty}`}
                    <span className="font-semibold text-[#1e242a]">
                      {" = $" + totalPrice.toFixed(2)}
                    </span>
                  </p>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <button
                  className="flex-1 rounded-xl bg-[#1e242a] py-3 font-semibold text-white shadow transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#37404a]"
                  onClick={addToCart}
                >
                  Add to Cart
                </button>

                <button
                  className="flex-1 rounded-xl border border-[#1e242a] bg-white py-3 font-semibold text-[#1e242a] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e5e8e6]"
                  onClick={buyNow}
                >
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        </div>

        <ProductDescriptionTabs
          otherDescription={p?.otherDescription}
          additionalInformation={p?.additionalInformation}
        />

        <RelatedProduct categoryId={categoryId} />
      </div>
    </>
  );
};

export default ShopDetail;